import { useState, useEffect } from "react";
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, Radio, Tv, X,
  Globe, Facebook, Instagram, Phone, GripVertical
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { API } from "@/App";
import axios from "axios";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const initialFormData = {
  name: "",
  type: "radio",
  description: "",
  stream_url: "",
  embed_type: "iframe",
  logo_image_id: "",
  logo_url: "",
  website: "",
  facebook: "",
  instagram: "",
  whatsapp: "",
  frequency: "",
  channel_number: "",
  is_local: true,
  status: "draft",
  order: 0,
  tags: "",
};

export default function AdminEnVivo() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaSearch, setMediaSearch] = useState("");

  useEffect(() => {
    fetchChannels();
  }, [page, limit]);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/live-channels?skip=${(page - 1) * limit}&limit=${limit}`);
      setChannels(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Error al cargar canales");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async (search = "") => {
    try {
      const response = await axios.get(`${API}/media?limit=50&search=${search}`);
      setMediaItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleNew = () => {
    setSelectedChannel(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleEdit = (channel) => {
    setSelectedChannel(channel);
    setFormData({
      name: channel.name || "",
      type: channel.type || "radio",
      description: channel.description || "",
      stream_url: channel.stream_url || "",
      embed_type: channel.embed_type || "iframe",
      logo_image_id: channel.logo_image_id || "",
      logo_url: channel.logo_url || "",
      website: channel.website || "",
      facebook: channel.facebook || "",
      instagram: channel.instagram || "",
      whatsapp: channel.whatsapp || "",
      frequency: channel.frequency || "",
      channel_number: channel.channel_number || "",
      is_local: channel.is_local ?? true,
      status: channel.status || "draft",
      order: channel.order || 0,
      tags: (channel.tags || []).join(", "),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!formData.stream_url.trim()) {
      toast.error("La URL de transmisión es obligatoria");
      return;
    }

    setSaving(true);
    try {
      if (selectedChannel) {
        await axios.put(`${API}/live-channels/${selectedChannel.channel_id}`, formData);
        toast.success("Canal actualizado");
      } else {
        await axios.post(`${API}/live-channels`, formData);
        toast.success("Canal creado");
      }
      setDialogOpen(false);
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedChannel) return;
    try {
      await axios.delete(`${API}/live-channels/${selectedChannel.channel_id}`);
      toast.success("Canal eliminado");
      setDeleteDialogOpen(false);
      setSelectedChannel(null);
      fetchChannels();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleStatus = async (channel) => {
    const newStatus = channel.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/live-channels/${channel.channel_id}`, {
        ...channel,
        status: newStatus,
        tags: (channel.tags || []).join(", "),
      });
      toast.success(newStatus === "published" ? "Canal publicado" : "Canal despublicado");
      fetchChannels();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const openMediaSelector = () => {
    fetchMedia();
    setMediaDialogOpen(true);
  };

  const selectMedia = (media) => {
    setFormData({ ...formData, logo_image_id: media.media_id, logo_url: media.url });
    setMediaDialogOpen(false);
  };

  const filtered = channels.filter((ch) => {
    const matchesSearch = ch.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || ch.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-en-vivo">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Radios y Canales de TV
            </h1>
            <p className="text-brand-textSecondary mt-1">
              Administra las radios y canales de TV en vivo
            </p>
          </div>
          <Button onClick={handleNew} className="rounded-full" data-testid="new-channel-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Canal
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar canales..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="channel-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="radio">Radios</SelectItem>
              <SelectItem value="tv">Canales de TV</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Canal</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Frecuencia/Canal</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Visitas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    No se encontraron canales
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((ch) => (
                  <TableRow key={ch.channel_id} data-testid={`channel-row-${ch.channel_id}`}>
                    <TableCell className="text-gray-400 text-sm">{ch.order}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {ch.logo_url ? (
                          <img src={getImageUrl(ch.logo_url)} alt={ch.name} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            {ch.type === "radio" ? <Radio className="h-5 w-5 text-gray-400" /> : <Tv className="h-5 w-5 text-gray-400" />}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{ch.name}</p>
                          {ch.is_local && <span className="text-xs text-emerald-600">Local</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ch.type === "radio" ? "border-purple-200 text-purple-700 bg-purple-50" : "border-blue-200 text-blue-700 bg-blue-50"}>
                        {ch.type === "radio" ? <Radio className="h-3 w-3 mr-1" /> : <Tv className="h-3 w-3 mr-1" />}
                        {ch.type === "radio" ? "Radio" : "TV"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {ch.type === "radio" ? ch.frequency || "—" : ch.channel_number || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className={ch.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                        {ch.status === "published" ? "Publicado" : "Borrador"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">{ch.view_count || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(ch)} title={ch.status === "published" ? "Despublicar" : "Publicar"}>
                          {ch.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(ch)} data-testid={`edit-channel-${ch.channel_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => { setSelectedChannel(ch); setDeleteDialogOpen(true); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedChannel ? "Editar Canal" : "Nuevo Canal"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Type & Name */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger data-testid="channel-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radio">Radio</SelectItem>
                    <SelectItem value="tv">Canal de TV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ej: Radio Progreso" data-testid="channel-name-input" />
              </div>
            </div>

            {/* Stream URL & Embed Type */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label>URL de Transmisi&oacute;n en Vivo *</Label>
                <Input value={formData.stream_url} onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })} placeholder="https://..." data-testid="channel-stream-url" />
                <p className="text-xs text-gray-400 mt-1">URL de la p&aacute;gina web donde se transmite en vivo</p>
              </div>
              <div>
                <Label>Tipo de Embed</Label>
                <Select value={formData.embed_type} onValueChange={(v) => setFormData({ ...formData, embed_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iframe">P&aacute;gina web (iframe)</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="audio">Audio directo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Descripci&oacute;n</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descripci&oacute;n breve del canal..." rows={2} />
            </div>

            {/* Frequency / Channel Number */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>{formData.type === "radio" ? "Frecuencia" : "N&uacute;mero de Canal"}</Label>
                <Input
                  value={formData.type === "radio" ? formData.frequency : formData.channel_number}
                  onChange={(e) => setFormData({
                    ...formData,
                    [formData.type === "radio" ? "frequency" : "channel_number"]: e.target.value
                  })}
                  placeholder={formData.type === "radio" ? "Ej: 98.5 FM" : "Ej: 11"}
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="published">Publicado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Logo */}
            <div>
              <Label>Logo del Canal</Label>
              <div className="flex items-center gap-3 mt-1">
                {formData.logo_url ? (
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={getImageUrl(formData.logo_url)} alt="Logo" className="w-full h-full object-cover" />
                    <button onClick={() => setFormData({ ...formData, logo_image_id: "", logo_url: "" })} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    {formData.type === "radio" ? <Radio className="h-6 w-6 text-gray-300" /> : <Tv className="h-6 w-6 text-gray-300" />}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={openMediaSelector}>
                  Seleccionar Logo
                </Button>
              </div>
            </div>

            {/* Contact & Social */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Sitio Web</Label>
                <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://..." />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} placeholder="+504..." />
              </div>
              <div>
                <Label>Facebook</Label>
                <Input value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })} placeholder="https://facebook.com/..." />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })} placeholder="https://instagram.com/..." />
              </div>
            </div>

            {/* Tags & Local */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Tags (separados por coma)</Label>
                <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="noticias, m&uacute;sica, deportes" />
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_local}
                    onChange={(e) => setFormData({ ...formData, is_local: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Canal local de Choluteca</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-channel-btn">
              {saving ? "Guardando..." : selectedChannel ? "Actualizar" : "Crear Canal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Selector Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Seleccionar Logo</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar im&aacute;genes..." value={mediaSearch} onChange={(e) => { setMediaSearch(e.target.value); fetchMedia(e.target.value); }} className="pl-10" />
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 overflow-y-auto max-h-[50vh]">
            {mediaItems.map((m) => (
              <div key={m.media_id} onClick={() => selectMedia(m)} className="aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-colors">
                <img src={getImageUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Canal</AlertDialogTitle>
            <AlertDialogDescription>
              &iquest;Est&aacute;s seguro de eliminar "{selectedChannel?.name}"? Esta acci&oacute;n no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="en-vivo-pagination"
          label="canales"
        />
    </AdminLayout>
  );
}
